import {
  Box,
  Button,
  Card,
  CardBody,
  CloseButton,
  DataList,
  Dialog,
  DrawerBody,
  DrawerFooter,
  Flex,
  Grid,
  GridItem,
  HStack,
  IconButton,
  Image,
  Link,
  Portal,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useParams } from '@tanstack/react-router';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { type Dispatch, type SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { HiOutlineInformationCircle } from 'react-icons/hi';

import { type ApiError, type IndicatorDetailsPublic, IndicatorsService } from '@/client';
import { Tooltip } from '@/components/ui/tooltip';
import useCustomToast from '@/hooks/use-custom-toast';

import { category } from '../IndicatorData';
import { DrawerItem } from './DrawerItem';

export const IndicatorDetails = ({
  indicatorData,
  setEditMode,
  editMode,
  switchToIndicator,
  onClose,
  editDisabled = false,
}: {
  indicatorData: IndicatorDetailsPublic;
  setEditMode: Dispatch<SetStateAction<boolean>>;
  editMode: boolean;
  switchToIndicator: (indicatorId: string) => void;
  onClose: () => void;
  editDisabled?: boolean;
}) => {
  const { slug } = useParams({ strict: false });
  const { t, i18n } = useTranslation();
  const showToast = useCustomToast();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const handleDeleteIndicator = () => {
    IndicatorsService.deleteIndicator({
      id: indicatorData?.id ?? '',
      companySlug: slug,
    })
      .then(() => {
        showToast(t('messages.toasts.success.title'), t('messages.toasts.success.indicatorDeleted'), 'success');
        setConfirmDeleteOpen(false);
        onClose();
      })
      .catch((err: ApiError) => {
        showToast(
          t('messages.toasts.failed.title'),
          err.status === 409
            ? t('messages.toasts.failed.indicatorInUse')
            : t('messages.toasts.failed.indicatorDeleted'),
          'error'
        );
        setConfirmDeleteOpen(false);
      });
  };

  return (
    <>
      <DrawerBody pb={20}>
        {indicatorData ? (
          <DataList.Root orientation="horizontal" divideY="1px" mb={4}>
            <DrawerItem title={t('indicator.columnsHeader.indicatorName')}>
              {indicatorData.name ?? (
                <Text color="gray.500" textStyle="sm">
                  N/R
                </Text>
              )}
            </DrawerItem>
            <DrawerItem title={t('indicator.columnsHeader.pillarName')}>
              {indicatorData.pillar_name ?? (
                <Text color="gray.500" textStyle="sm">
                  N/R
                </Text>
              )}
            </DrawerItem>
            <DrawerItem title={t('indicator.columnsHeader.commitmentName')}>
              {indicatorData.commitment_name ?? (
                <Text color="gray.500" textStyle="sm">
                  N/R
                </Text>
              )}
            </DrawerItem>
            <DrawerItem title={t('indicator.columnsHeader.description')}>
              {indicatorData.description ? (
                <Text className="ql-editor ql-viewer" as="div">
                  {parse(DOMPurify.sanitize(indicatorData.description))}
                </Text>
              ) : (
                <Text color="gray.500" textStyle="sm">
                  N/R
                </Text>
              )}
            </DrawerItem>

            <DrawerItem title={t('indicator.columnsHeader.department')}>
              {indicatorData.department ?? (
                <Text color="gray.500" textStyle="sm">
                  N/R
                </Text>
              )}
            </DrawerItem>

            <DrawerItem title={t('indicator.columnsHeader.categoryName')}>
              {(category.items.find((item) => item.value === indicatorData.category)?.label || '') ?? (
                <Text color="gray.500" textStyle="sm">
                  N/R
                </Text>
              )}
            </DrawerItem>

            {indicatorData.category === 'composite' && (
              <>
                <DrawerItem title={t('indicator.columnsHeader.formula')}>
                  {indicatorData.formula ?? (
                    <Text color="gray.500" textStyle="sm">
                      N/R
                    </Text>
                  )}
                </DrawerItem>
                <DrawerItem title={t('indicator.columnsHeader.formulaUnits')}>
                  {indicatorData.formula_units ?? (
                    <Text color="gray.500" textStyle="sm">
                      N/R
                    </Text>
                  )}
                </DrawerItem>
              </>
            )}
            {indicatorData.category !== 'composite' && (
              <DrawerItem title={t('indicator.columnsHeader.unit')}>
                {indicatorData.unit ?? (
                  <Text color="gray.500" textStyle="sm">
                    N/R
                  </Text>
                )}
              </DrawerItem>
            )}

            {indicatorData.depends_on && indicatorData.depends_on.length > 0 && (
              <DrawerItem title={t('indicator.columnsHeader.dependsOn')}>
                <Stack gap="4">
                  {indicatorData.depends_on.map((indicator) => (
                    <HStack key={indicator.name} gap="2">
                      <Stack gap="0">
                        <Link variant="underline" fontWeight="medium" onClick={() => switchToIndicator(indicator.id)}>
                          {indicator.name}
                        </Link>
                      </Stack>
                    </HStack>
                  ))}
                </Stack>
              </DrawerItem>
            )}
            {indicatorData.used_by && indicatorData.used_by.length > 0 && (
              <DrawerItem title={t('indicator.columnsHeader.usedBy')}>
                <Stack gap="4">
                  {indicatorData.used_by.map((indicator) => (
                    <HStack key={indicator.name} gap="2">
                      <Stack gap="0">
                        <Link variant="underline" fontWeight="medium" onClick={() => switchToIndicator(indicator.id)}>
                          {indicator.name}
                        </Link>
                      </Stack>
                    </HStack>
                  ))}
                </Stack>
              </DrawerItem>
            )}

            <DrawerItem title={t('indicator.columnsHeader.responsibles')}>
              <Stack gap="8">
                {indicatorData.responsibles.map((user) => (
                  <HStack key={user.email} gap="4">
                    <Stack gap="0">
                      <Text fontWeight="medium">{`${user.first_name} ${user.last_name}`}</Text>
                      <Text color="fg.muted" textStyle="sm">
                        {user.email}
                      </Text>
                    </Stack>
                  </HStack>
                ))}
                {indicatorData.responsibles.length === 0 && (
                  <Text color="gray.500" textStyle="sm">
                    N/R
                  </Text>
                )}
              </Stack>
            </DrawerItem>
            <DataList.Item pt="4">
              <DataList.ItemLabel>
                {t('indicator.columnsHeader.sdgs')}
                <Tooltip
                  showArrow
                  openDelay={100}
                  closeDelay={100}
                  content={t('indicator.tooltip.sdgs.noEdit')}
                  contentProps={{ css: { '--tooltip-bg': 'colors.primary' } }}
                >
                  <IconButton variant="ghost" aria-label="info" size="2xs" colorPalette="gray">
                    <HiOutlineInformationCircle />
                  </IconButton>
                </Tooltip>
              </DataList.ItemLabel>
              <DataList.ItemValue>
                <Grid
                  templateColumns={{
                    md: 'repeat(6, 1fr)',
                    base: 'repeat(4, 1fr)',
                  }}
                  gap="4"
                >
                  {indicatorData.sdgs.map((sdg) => (
                    <GridItem key={sdg.code}>
                      <Image
                        rounded="md"
                        src={`/assets/images/sdgs/${i18n.language}/sdg_${sdg.code}.png`}
                        alt={sdg.name}
                        w={80}
                      />
                    </GridItem>
                  ))}
                  {indicatorData.sdgs.length === 0 && (
                    <Text color="gray.500" textStyle="sm">
                      N/R
                    </Text>
                  )}
                </Grid>
              </DataList.ItemValue>
            </DataList.Item>

            <DrawerItem title={t('indicator.columnsHeader.referentials')}>
              <VStack alignItems={'start'}>
                {indicatorData.referentials.map((referential) => (
                  <VStack key={referential.id} gap={0} alignItems={'start'}>
                    {referential.global_referential && (
                      <Text color="gray.900" textStyle="md">
                        {referential.global_referential.name}
                      </Text>
                    )}
                    {referential.global_referential_category && (
                      <Text color="gray.700" textStyle="sm">
                        - {referential.global_referential_category.name}
                      </Text>
                    )}
                    {referential.global_referential_sub_category && (
                      <Text color="gray.500" textStyle="sm">
                        -- {referential.global_referential_sub_category.name}
                      </Text>
                    )}
                  </VStack>
                ))}
                {indicatorData.referentials.length === 0 && (
                  <Text color="gray.500" textStyle="sm">
                    N/R
                  </Text>
                )}
              </VStack>
            </DrawerItem>

            <DrawerItem title={t('indicator.columnsHeader.externalReferentialsKeys')}>
              <VStack alignItems={'start'}>
                {indicatorData.referentials_keys
                  .sort((a, b) => {
                    if (a.referential < b.referential) return -1;
                    if (a.referential > b.referential) return 1;
                    if (a.key < b.key) return -1;
                    if (a.key > b.key) return 1;
                    return 0;
                  })
                  .map((referentialKey) => (
                    <VStack key={referentialKey.referential + referentialKey.key} gap={0} alignItems={'start'}>
                      <Text color="gray.900" textStyle="sm">
                        <b>{referentialKey.referential}</b> : {referentialKey.key}
                      </Text>
                    </VStack>
                  ))}
                {indicatorData.referentials_keys.length === 0 && (
                  <Text color="gray.500" textStyle="sm">
                    N/R
                  </Text>
                )}
              </VStack>
            </DrawerItem>
          </DataList.Root>
        ) : (
          <Card.Root>
            <CardBody>{t('indicator.noSelection')}</CardBody>
          </Card.Root>
        )}
      </DrawerBody>
      <DrawerFooter pb={6}>
        {!editMode && (
          <Flex justify="space-between" wrap={{ base: 'wrap-reverse', md: 'no-wrap' }} align="center" w="100%">
            <Text color="gray.500" pt={2}>
              {indicatorData?.updated_at && (
                <>
                  {t('suggestion.columnsHeader.updatedAt')} :{' '}
                  {!editDisabled && <Box as="br" display={{ base: 'none', md: 'block' }} />}
                  {new Date(indicatorData.updated_at).toLocaleDateString('fr-FR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </>
              )}
            </Text>

            {!editDisabled && (
              <Flex gap={4} ml={'auto'}>
                <Dialog.Root
                  role="alertdialog"
                  placement="center"
                  lazyMount
                  open={confirmDeleteOpen}
                  onOpenChange={(e) => setConfirmDeleteOpen(e.open)}
                >
                  <Dialog.Trigger asChild>
                    <Button bg="red.500" _hover={{ bg: 'red.800' }}>
                      <FaTrash /> {t('indicator.drawer.delete.title')}
                    </Button>
                  </Dialog.Trigger>
                  <Portal>
                    <Dialog.Backdrop zIndex={9999} />
                    <Dialog.Positioner zIndex={9999}>
                      <Dialog.Content>
                        <Dialog.Header>
                          <Dialog.Title>{t('messages.headers.areYouSure')}</Dialog.Title>
                        </Dialog.Header>
                        <Dialog.Body>
                          <p>{t('messages.description.delete.generic')}</p>
                        </Dialog.Body>
                        <Dialog.Footer>
                          <Dialog.ActionTrigger asChild>
                            <Button variant="outline">Cancel</Button>
                          </Dialog.ActionTrigger>
                          <Button colorPalette="red" onClick={handleDeleteIndicator}>
                            Delete
                          </Button>
                        </Dialog.Footer>
                        <Dialog.CloseTrigger asChild>
                          <CloseButton size="sm" />
                        </Dialog.CloseTrigger>
                      </Dialog.Content>
                    </Dialog.Positioner>
                  </Portal>
                </Dialog.Root>

                <Button variant="outline" onClick={() => setEditMode(true)}>
                  <FaEdit /> {t('indicator.drawer.edit.title')}
                </Button>
              </Flex>
            )}
          </Flex>
        )}
      </DrawerFooter>
    </>
  );
};
